import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, ImageOff, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import ProductForm from "@/components/admin/ProductForm";
import { imgUrl } from "@/lib/productImages";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, "new" = create, product object = edit
  const [filter, setFilter] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
    } finally {
      setLoading(false);
    }
  }

  async function togglePublished(p) {
    try {
      const { data } = await api.patch(`/admin/products/${p.slug}`, {
        published: !(p.published !== false),
      });
      setProducts((prev) => prev.map((it) => (it.slug === p.slug ? data : it)));
      setFlash(data.published ? `${p.name} published.` : `${p.name} hidden.`);
      setTimeout(() => setFlash(""), 2500);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Toggle failed.");
    }
  }

  function onSaved(_, verb) {
    setEditing(null);
    setFlash(`Product ${verb}.`);
    refresh();
    setTimeout(() => setFlash(""), 2500);
  }

  async function handleDelete(slug) {
    if (!window.confirm(`Delete ${slug}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${slug}`);
      setFlash(`Product deleted.`);
      refresh();
      setTimeout(() => setFlash(""), 2500);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Delete failed.");
    }
  }

  const filtered = useMemo(() => {
    if (!filter) return products;
    const q = filter.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
    );
  }, [products, filter]);

  if (editing) {
    return (
      <div data-testid="admin-products-edit">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493] mb-3">// Products / {editing === "new" ? "New" : "Edit"}</div>
        <ProductForm
          existing={editing === "new" ? null : editing}
          onSaved={onSaved}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div data-testid="admin-products-list">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">// Products</div>
          <h2 className="mt-2 font-display text-2xl md:text-4xl tracking-tight">Catalog</h2>
        </div>
        <div className="flex items-center gap-3">
          <input
            data-testid="admin-products-filter"
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name, slug, sku, category"
            className="field !py-2 !text-[12px] min-w-[260px]"
          />
          <button
            data-testid="admin-products-new"
            type="button"
            onClick={() => setEditing("new")}
            className="btn-primary !py-2 !px-4"
          >
            <Plus size={14} strokeWidth={1.5} /> New Product
          </button>
        </div>
      </div>

      {flash && (
        <div
          data-testid="admin-products-flash"
          className="mb-4 font-mono text-[11px] tracking-[0.22em] uppercase text-[#00ff41]"
        >
          // {flash}
        </div>
      )}

      <div className="border border-[#2a1a3a] overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="admin-products-table">
          <thead>
            <tr className="bg-[#06000e]">
              {["", "Name", "SKU", "Category", "Price", "Status", "Visibility", "Actions"].map((h) => (
                <th
                  key={h}
                  className="text-left font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493] px-3 py-3 border-b border-[#2a1a3a]"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-8 font-mono text-xs tracking-[0.2em] uppercase text-[#7a6a90]">
                  // loading...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 font-mono text-xs tracking-[0.2em] uppercase text-[#7a6a90]">
                  // no products match
                </td>
              </tr>
            )}
            {filtered.map((p) => {
              const isPublished = p.published !== false;
              return (
                <tr
                  key={p.slug}
                  data-testid={`admin-products-row-${p.slug}`}
                  className={`border-b border-[#1a0a2a] last:border-b-0 hover:bg-[#0a0316]/60 ${
                    !isPublished ? "opacity-60" : ""
                  }`}
                >
                <td className="px-3 py-3 align-middle w-[64px]">
                  {p.images && p.images[0] ? (
                    <img src={imgUrl(p.images[0])} alt="" className="w-12 object-cover ring-1 ring-[#2a1a3a]" style={{ aspectRatio: "4/5" }} />
                  ) : (
                    <div className="w-12 h-[60px] bg-[#0a0316] ring-1 ring-[#2a1a3a] flex items-center justify-center text-[#7a6a90]">
                      <ImageOff size={14} strokeWidth={1.2} />
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 align-middle">
                  <div className="font-display text-sm leading-tight">{p.name}</div>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-[#7a6a90] flex items-center gap-2 flex-wrap">
                    <span>{p.slug}</span>
                    <span className="text-[#7a6a90]/60">·</span>
                    <span
                      data-testid={`admin-products-tier-${p.slug}`}
                      className={
                        p.tier === "contraband"
                          ? "text-[#ff3b30]/90 tracking-[0.18em]"
                          : "text-[#c084fc]/80 tracking-[0.18em]"
                      }
                    >
                      {(p.tier || "mainline").toUpperCase()}
                    </span>
                    <span className="text-[#7a6a90]/60">·</span>
                    <span
                      data-testid={`admin-products-collection-${p.slug}`}
                      className="text-[#7a6a90] tracking-[0.18em]"
                    >
                      {p.collection || "—"}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3 align-middle font-mono text-[12px] text-white">{p.sku}</td>
                <td className="px-3 py-3 align-middle font-mono text-[11px] tracking-[0.1em] uppercase text-[#c084fc]">
                  {p.category}
                  {p.subcategory && <div className="text-[10px] text-[#7a6a90] mt-0.5">{p.subcategory}</div>}
                </td>
                <td className="px-3 py-3 align-middle font-mono text-[12px] text-white">
                  {p.price ? `$${p.price}` : "—"}
                </td>
                <td className="px-3 py-3 align-middle font-mono text-[11px] tracking-[0.1em] uppercase">
                  <span className={p.tier === "contraband" ? "text-[#ff3b30]" : "text-[#c084fc]"}>
                    {p.status_badge || p.status}
                  </span>
                </td>
                <td className="px-3 py-3 align-middle">
                  <button
                    type="button"
                    data-testid={`admin-products-toggle-${p.slug}`}
                    onClick={() => togglePublished(p)}
                    aria-pressed={isPublished}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                      isPublished
                        ? "border-[#00ff41]/50 text-[#00ff41] hover:border-[#00ff41]"
                        : "border-[#7a6a90] text-[#7a6a90] hover:border-white hover:text-white"
                    }`}
                  >
                    {isPublished ? (
                      <>
                        <Eye size={11} strokeWidth={1.5} /> Published
                      </>
                    ) : (
                      <>
                        <EyeOff size={11} strokeWidth={1.5} /> Hidden
                      </>
                    )}
                  </button>
                </td>
                <td className="px-3 py-3 align-middle">
                  <div className="flex gap-2">
                    <button
                      data-testid={`admin-products-edit-${p.slug}`}
                      onClick={() => setEditing(p)}
                      className="btn-ghost !p-2"
                      aria-label="Edit"
                    >
                      <Pencil size={13} strokeWidth={1.5} />
                    </button>
                    <button
                      data-testid={`admin-products-delete-${p.slug}`}
                      onClick={() => handleDelete(p.slug)}
                      className="btn-ghost !p-2 hover:!text-[#ff3b30] hover:!border-[#ff3b30]"
                      aria-label="Delete"
                    >
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
